import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  prefersReducedMotion,
  prefersHighContrast,
  getColorSchemePreference,
  announceToScreenReader,
} from '@/utils/accessibility';

interface AccessibilitySettings {
  reducedMotion: boolean;
  highContrast: boolean;
  colorScheme: 'light' | 'dark' | 'auto';
  fontSize: 'small' | 'medium' | 'large';
  focusVisible: boolean;
  screenReaderAnnouncements: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => void;
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  resetToDefaults: () => void;
}

const defaultSettings: AccessibilitySettings = {
  reducedMotion: false,
  highContrast: false,
  colorScheme: 'auto',
  fontSize: 'medium',
  focusVisible: true,
  screenReaderAnnouncements: true,
};

const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error(
      'useAccessibility must be used within an AccessibilityProvider'
    );
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const stored = localStorage.getItem('accessibility-settings');
    if (stored) {
      try {
        return { ...defaultSettings, ...JSON.parse(stored) };
      } catch {
        return defaultSettings;
      }
    }

    return {
      ...defaultSettings,
      reducedMotion: prefersReducedMotion(),
      highContrast: prefersHighContrast(),
      colorScheme:
        getColorSchemePreference() === 'no-preference'
          ? 'auto'
          : getColorSchemePreference(),
    };
  });

  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    const root = document.documentElement;

    if (settings.reducedMotion) {
      root.style.setProperty('--animation-duration', '0.01ms');
      root.style.setProperty('--transition-duration', '0.01ms');
    } else {
      root.style.removeProperty('--animation-duration');
      root.style.removeProperty('--transition-duration');
    }

    const fontSizeMap = {
      small: '14px',
      medium: '16px',
      large: '18px',
    };
    root.style.setProperty('--base-font-size', fontSizeMap[settings.fontSize]);

    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    if (settings.colorScheme === 'auto') {
      root.classList.remove('light', 'dark');
      // Let system preference take over
    } else {
      root.classList.remove('light', 'dark');
      root.classList.add(settings.colorScheme);
    }

    if (settings.focusVisible) {
      root.classList.add('focus-visible');
    } else {
      root.classList.remove('focus-visible');
    }
  }, [settings]);

  useEffect(() => {
    const mediaQueries = [
      window.matchMedia('(prefers-reduced-motion: reduce)'),
      window.matchMedia('(prefers-contrast: high)'),
      window.matchMedia('(prefers-color-scheme: dark)'),
      window.matchMedia('(prefers-color-scheme: light)'),
    ];

    const handleChange = () => {
      if (settings.colorScheme === 'auto') {
        const newColorScheme = getColorSchemePreference();
        if (newColorScheme !== 'no-preference') {
          // Don't update state, just apply the class
          const root = document.documentElement;
          root.classList.remove('light', 'dark');
          root.classList.add(newColorScheme);
        }
      }
    };

    mediaQueries.forEach((mq) => mq.addEventListener('change', handleChange));

    return () => {
      mediaQueries.forEach((mq) =>
        mq.removeEventListener('change', handleChange)
      );
    };
  }, [settings.colorScheme]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));

    if (settings.screenReaderAnnouncements) {
      const settingNames = {
        reducedMotion: 'Reduced motion',
        highContrast: 'High contrast',
        colorScheme: 'Color scheme',
        fontSize: 'Font size',
        focusVisible: 'Focus indicators',
        screenReaderAnnouncements: 'Screen reader announcements',
      };

      announceToScreenReader(
        `${settingNames[key]} ${value === true ? 'enabled' : value === false ? 'disabled' : `set to ${value}`}`,
        'polite'
      );
    }
  };

  const announce = (
    message: string,
    priority: 'polite' | 'assertive' = 'polite'
  ) => {
    if (settings.screenReaderAnnouncements) {
      announceToScreenReader(message, priority);
    }
  };

  const resetToDefaults = () => {
    setSettings(defaultSettings);
    announce('Accessibility settings reset to defaults', 'polite');
  };

  return (
    <AccessibilityContext.Provider
      value={{
        settings,
        updateSetting,
        announce,
        resetToDefaults,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};
